// Chemin: C:\gestion-copro\src\pages\vigie\AffaireFormVigiePage.tsx
// Session 233 : CORRECTION BUG — fichier était une copie de CourrierFormVigiePage
//               Transposition correcte de AffaireFormPage CS → VIGIE
//               Couleur ambre, routes /vigie/dossiers/*, useCopropriete, pas d'interlocuteurs CS

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { AlertCircle } from 'lucide-react';
import {
  CreateAffaireData,
  TypeAffaire,
  PrioriteAffaire,
  GarantieApplicable,
  TYPE_AFFAIRE_LABELS,
  PRIORITE_AFFAIRE_LABELS,
  GARANTIE_LABELS
} from '../../types/affaire.types';
import { createAffaire, getAffaireById, updateAffaire } from '../../services/affaire.service';

export default function AffaireFormVigiePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userProfile, firebaseUser } = useAuth();
  const { coproprieteId } = useCopropriete();
  const isEdition = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdition);
  const [error, setError] = useState<string | null>(null);

  const [titre, setTitre] = useState('');
  const [type, setType] = useState<TypeAffaire>('litige_construction');
  const [priorite, setPriorite] = useState<PrioriteAffaire>('normale');
  const [description, setDescription] = useState('');
  const [garantieApplicable, setGarantieApplicable] = useState<GarantieApplicable | ''>('');
  const [montantEstime, setMontantEstime] = useState<string>('');

  // Charger les données en mode édition
  useEffect(() => {
    if (!id) return;

    const loadData = async () => {
      try {
        const affaire = await getAffaireById(id);
        if (affaire) {
          setTitre(affaire.titre);
          setType(affaire.type);
          setPriorite(affaire.priorite);
          setDescription(affaire.description);
          setGarantieApplicable(affaire.garantieApplicable || '');
          setMontantEstime(affaire.montantEstime?.toString() || '');
        }
      } catch (err) {
        console.error('Erreur chargement dossier:', err);
        setError('Impossible de charger le dossier');
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!coproprieteId || !firebaseUser) {
      setError('Session invalide');
      return;
    }

    if (!titre.trim()) {
      setError('Le titre est obligatoire');
      return;
    }

    if (!description.trim()) {
      setError('La description est obligatoire');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: CreateAffaireData = {
        coproprieteId,
        titre: titre.trim(),
        type,
        description: description.trim(),
        priorite,
        interlocuteurIds: []
      };

      if (garantieApplicable) {
        data.garantieApplicable = garantieApplicable as GarantieApplicable;
      }
      if (montantEstime) {
        data.montantEstime = parseFloat(montantEstime);
      }

      if (isEdition && id) {
        await updateAffaire(id, {
          titre: data.titre,
          type: data.type,
          priorite: data.priorite,
          description: data.description,
          interlocuteurIds: [],
          garantieApplicable: garantieApplicable ? garantieApplicable as GarantieApplicable : undefined,
          montantEstime: montantEstime ? parseFloat(montantEstime) : undefined
        });
        navigate(`/vigie/dossiers/${id}`);
      } else {
        const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
        const newId = await createAffaire(data, firebaseUser.uid, userNom);
        navigate(`/vigie/dossiers/${newId}`);
      }
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const showGarantie = type === 'litige_construction';

  if (!coproprieteId) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Aucune copropriété associée à votre compte.</p>
        </div>
      </div>
    );
  }

  if (loadingData) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* En-tête */}
      <div>
        <button
          onClick={() => navigate('/vigie/dossiers')}
          className="text-gray-600 hover:text-gray-900 mb-2 inline-flex items-center gap-1 text-sm"
        >
          ← Retour aux dossiers
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdition ? 'Modifier le dossier' : 'Nouveau dossier'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEdition
            ? 'Modifiez les informations du dossier.'
            : 'Créez un dossier pour regrouper vos signalements, documents et courriers autour d\'un même sujet.'}
        </p>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Titre */}
        <div>
          <label htmlFor="titre" className="block text-sm font-medium text-gray-700 mb-1">
            Titre du dossier *
          </label>
          <input
            type="text"
            id="titre"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            placeholder="Ex: Infiltrations toiture — Bâtiment principal"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            required
          />
        </div>

        {/* Type et Priorité */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Type de dossier *
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as TypeAffaire)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              {(Object.entries(TYPE_AFFAIRE_LABELS) as [TypeAffaire, string][]).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="priorite" className="block text-sm font-medium text-gray-700 mb-1">
              Priorité
            </label>
            <select
              id="priorite"
              value={priorite}
              onChange={(e) => setPriorite(e.target.value as PrioriteAffaire)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              {(Object.entries(PRIORITE_AFFAIRE_LABELS) as [PrioriteAffaire, string][]).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Garantie (si litige construction) */}
        {showGarantie && (
          <div>
            <label htmlFor="garantie" className="block text-sm font-medium text-gray-700 mb-1">
              Garantie applicable
            </label>
            <select
              id="garantie"
              value={garantieApplicable}
              onChange={(e) => setGarantieApplicable(e.target.value as GarantieApplicable | '')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="">Non déterminée</option>
              {(Object.entries(GARANTIE_LABELS) as [GarantieApplicable, string][]).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              GPA : 1 an après réception · Biennale : 2 ans · Décennale : 10 ans
            </p>
          </div>
        )}

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description *
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="Décrivez le contexte, les faits constatés, les enjeux..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            required
          />
        </div>

        {/* Montant estimé */}
        <div>
          <label htmlFor="montant" className="block text-sm font-medium text-gray-700 mb-1">
            Montant estimé (optionnel)
          </label>
          <div className="relative">
            <input
              type="number"
              id="montant"
              value={montantEstime}
              onChange={(e) => setMontantEstime(e.target.value)}
              placeholder="0"
              min="0"
              step="0.01"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 pr-12"
            />
            <span className="absolute right-4 top-2 text-gray-500">EUR</span>
          </div>
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/vigie/dossiers')}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Enregistrement...' : (isEdition ? 'Enregistrer' : 'Créer le dossier')}
          </button>
        </div>
      </form>
    </div>
  );
}
